import { CATEGORIES, isDeviceKind } from "@dransay/api/models";
import { cva, type VariantProps } from "class-variance-authority";
import { clsx } from "clsx";
import { useTranslation } from "react-i18next";
import { useCurrencyPrice } from "~/hooks/use-currency-price.hook";

const priceVariants = cva("whitespace-nowrap", {
	variants: {
		variant: {
			raw: "",
			default: "text-ansay-main text-h4",
			responsive: "text-ansay-main text-body-bold md:text-h4",
		},
	},
	defaultVariants: {
		variant: "default",
	},
});

export function Price({
	price,
	variant = "default",
	className,
	withFromLabel,
	category,
	kind,
}: {
	price: number;
	variant?: VariantProps<typeof priceVariants>["variant"];
	className?: string;
	withFromLabel?: boolean;
	category: string | null;
	kind?: string | null;
}) {
	const { t } = useTranslation();
	const totalPrice = category === CATEGORIES.DRINKABLES ? price * 6 : price;
	const { currencyPrice } = useCurrencyPrice(totalPrice);

	const translationKey = (() => {
		if (category === CATEGORIES.INHALATIONS_EXTRACT && isDeviceKind(kind)) {
			return withFromLabel ? "pricePerPieceFrom" : "pricePerPiece";
		}

		switch (category) {
			case CATEGORIES.FLOWERS:
				return withFromLabel ? "pricePerGramFrom" : "pricePerGram";
			case CATEGORIES.EDIBLES:
				return withFromLabel ? "pricePerPieceFrom" : "pricePerPiece";
			case CATEGORIES.DRINKABLES:
				return withFromLabel ? "pricePer6PieceFrom" : "pricePer6Piece";
			case CATEGORIES.CAPSULES:
			case CATEGORIES.CANNABIS_ACCESSORIES:
				return withFromLabel ? "pricePerPieceFrom" : "pricePerPiece";
			default:
				return withFromLabel ? "pricePerMilliliterFrom" : "pricePerMilliliter";
		}
	})();

	return (
		<div
			className={clsx(priceVariants({ variant }), className)}
			data-testid="product-price"
			suppressHydrationWarning
		>
			{t(translationKey, { price: currencyPrice })}
		</div>
	);
}
